/* eslint-disable react/prop-types */
import { FC, ReactNode, useEffect } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import isEmpty from "lodash.isempty";

interface Props {
	defaultValues: GroupFormValues;
	onSubmit: (data: GroupFormValues) => void;
	isSubmitting?: boolean;
	isEdit: boolean;
	onDelete?: () => void;
	backPath: string;
	extraContent?: ReactNode;
}

const GroupForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting = false,
	isEdit,
	onDelete,
	backPath,
	extraContent
}) => {
	const methods = useForm<GroupFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch } = methods;
	const { isDirty, errors } = useFormState({ control });

	const isSubmitBtnDisabled = !isDirty || !isEmpty(errors) || isSubmitting;

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues, reset]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Details */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.name ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.name && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.name.message ?? "Please fill out the required field"}
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Details
							</label>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="name"
									rules={{
										required: "Name is required"
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Name
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Group name"
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="description"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Description (optional)
											</FormLabel>
											<FormControl>
												<textarea
													placeholder="Group description"
													rows={4}
													className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden resize-none"
													onInput={(e) => {
														const target = e.target as HTMLTextAreaElement;
														target.style.height = "auto";
														target.style.height = `${target.scrollHeight}px`;
													}}
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-600 text-right mt-1">
												{watch("description")?.length || 0} characters
											</p>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{extraContent}
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to={backPath}
							className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to groups
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button type="submit" disabled={isSubmitBtnDisabled}>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : isEdit ? (
							"Save"
						) : (
							"Create"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default GroupForm;
